var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
/**
  * vee-validate v4.15.1
  * (c) 2025 Abdelrahman Awad
  * @license MIT
  */
import { ZodObject, ZodDefault, ZodFirstPartyTypeKind } from 'zod';
import { isNotNestedPath, cleanupNonNestedPath } from 'vee-validate';
var isObject = function (obj) { return obj !== null && !!obj && typeof obj === 'object' && !Array.isArray(obj); };
function isIndex(value) {
    return Number(value) >= 0;
}
function isObjectLike(value) {
    return typeof value === 'object' && value !== null;
}
function getTag(value) {
    if (value == null) {
        return value === undefined ? '[object Undefined]' : '[object Null]';
    }
    return Object.prototype.toString.call(value);
}
// Reference: https://github.com/lodash/lodash/blob/master/isPlainObject.js
function isPlainObject(value) {
    if (!isObjectLike(value) || getTag(value) !== '[object Object]') {
        return false;
    }
    if (Object.getPrototypeOf(value) === null) {
        return true;
    }
    var proto = value;
    while (Object.getPrototypeOf(proto) !== null) {
        proto = Object.getPrototypeOf(proto);
    }
    return Object.getPrototypeOf(value) === proto;
}
function merge(target, source) {
    Object.keys(source).forEach(function (key) {
        if (isPlainObject(source[key]) && isPlainObject(target[key])) {
            if (!target[key]) {
                target[key] = {};
            }
            merge(target[key], source[key]);
            return;
        }
        target[key] = source[key];
    });
    return target;
}
/**
 * Constructs a path with dot paths for arrays to use brackets to be compatible with vee-validate path syntax
 */
function normalizeFormPath(path) {
    var pathArr = path.split('.');
    if (!pathArr.length) {
        return '';
    }
    var fullPath = String(pathArr[0]);
    for (var i = 1; i < pathArr.length; i++) {
        if (isIndex(pathArr[i])) {
            fullPath += "[".concat(pathArr[i], "]");
            continue;
        }
        fullPath += ".".concat(pathArr[i]);
    }
    return fullPath;
}
/**
 * Transforms a Zod object schema to Yup's schema
 */
function toTypedSchema(zodSchema, opts) {
    var schema = {
        __type: 'VVTypedSchema',
        parse: function (value) {
            return __awaiter(this, void 0, void 0, function () {
                var result, errors;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0: return [4 /*yield*/, zodSchema.safeParseAsync(value, opts)];
                        case 1:
                            result = _b.sent();
                            if (result.success) {
                                return [2 /*return*/, {
                                        value: result.data,
                                        errors: [],
                                    }];
                            }
                            errors = {};
                            processIssues(result.error.issues, errors);
                            return [2 /*return*/, {
                                    errors: Object.values(errors),
                                }];
                    }
                });
            });
        },
        cast: function (values) {
            try {
                return zodSchema.parse(values);
            }
            catch (_a) {
                // Zod does not support "casting" or not validating a value, so next best thing is getting the defaults and merging them with the provided values.
                var defaults = getDefaults(zodSchema);
                if (isObject(defaults) && isObject(values)) {
                    return merge(defaults, values);
                }
                return values;
            }
        },
        describe: function (path) {
            try {
                if (!path) {
                    return {
                        required: !zodSchema.isOptional(),
                        exists: true,
                    };
                }
                var description = getSchemaForPath(path, zodSchema);
                if (!description) {
                    return {
                        required: false,
                        exists: false,
                    };
                }
                return {
                    required: !description.isOptional(),
                    exists: true,
                };
            }
            catch (_a) {
                if ((process.env.NODE_ENV !== 'production')) {
                    // eslint-disable-next-line no-console
                    console.warn("Failed to describe path ".concat(path, " on the schema, returning a default description."));
                }
                return {
                    required: false,
                    exists: false,
                };
            }
        },
    };
    return schema;
}
function processIssues(issues, errors) {
    issues.forEach(function (issue) {
        var path = normalizeFormPath(issue.path.join('.'));
        if (issue.code === 'invalid_union') {
            processIssues(issue.unionErrors.flatMap(function (ue) { return ue.issues; }), errors);
            if (!path) {
                return;
            }
        }
        if (!errors[path]) {
            errors[path] = { errors: [], path: path };
        }
        errors[path].errors.push(issue.message);
    });
}
// Zod does not support extracting default values so the next best thing is manually extracting them.
// https://github.com/colinhacks/zod/issues/1944#issuecomment-1406566175
function getDefaults(schema) {
    if (!(schema instanceof ZodObject)) {
        return undefined;
    }
    return Object.fromEntries(Object.entries(schema.shape).map(function (_b) {
        var _c = __read(_b, 2), key = _c[0], value = _c[1];
        if (value instanceof ZodDefault) {
            return [key, value._def.defaultValue()];
        }
        if (value instanceof ZodObject) {
            return [key, getDefaults(value)];
        }
        return [key, undefined];
    }));
}
/**
 * @deprecated use toTypedSchema instead.
 */
var toFieldValidator = toTypedSchema;
/**
 * @deprecated use toTypedSchema instead.
 */
var toFormValidator = toTypedSchema;
function getSchemaForPath(path, schema) {
    if (!isObjectSchema(schema)) {
        return null;
    }
    if (isNotNestedPath(path)) {
        return schema.shape[cleanupNonNestedPath(path)];
    }
    var paths = (path || '').split(/\.|\[(\d+)\]/).filter(Boolean);
    var currentSchema = schema;
    for (var i = 0; i <= paths.length; i++) {
        var p = paths[i];
        if (!p || !currentSchema) {
            return currentSchema;
        }
        if (isObjectSchema(currentSchema)) {
            currentSchema = currentSchema.shape[p] || null;
            continue;
        }
        if (isIndex(p) && isArraySchema(currentSchema)) {
            currentSchema = currentSchema._def.type;
        }
    }
    return null;
}
function getDefType(schema) {
    return schema._def.typeName;
}
function isArraySchema(schema) {
    return getDefType(schema) === ZodFirstPartyTypeKind.ZodArray;
}
function isObjectSchema(schema) {
    return getDefType(schema) === ZodFirstPartyTypeKind.ZodObject;
}
export { toFieldValidator, toFormValidator, toTypedSchema };
